import { connect } from "react-redux";

import { shouldHideAmenitiesSelection } from "@app/helpers/amenities";
import {
  getDefaultTripId,
  getDefaultTripIdByJourneyId,
  isBoXTrip
} from "@app/helpers/udm";
import { getLegCardById } from "@app/modules/results/selectors/leg-cards/get-leg-card-by-id";
import { getLegCardOptionsMatrixForJourney } from "@app/modules/results/selectors/leg-cards/get-options";
import { areTripStopsLoaded } from "@app/modules/results/selectors/trips/are-trip-stops-loaded";
import { getTripById } from "@app/modules/results/selectors/trips/get-trip";
import type { ResultsReduxState } from "@app/types/results-redux-types";
import type { TripOptionsMatrix } from "@app/types/results-types";
import type { SearchInfo } from "@app/types/search-types";

import type { ShuttleDestination } from "../results/selectors/leg-cards/get-unique-destinations-for-shuttle-leg-card";
import type { ConnectedProps } from "react-redux";

import { TicketOptions as TicketOptionsComponent } from "./ticket-options";
import { getOperatorProfileRatingById } from "../results/selectors/get-profile-rating-by-id";
import { getResultSearchCount } from "../results/selectors/get-result-search-count";
import { getUniqueDestinationsForShuttleLegCard } from "../results/selectors/leg-cards/get-unique-destinations-for-shuttle-leg-card";
import {
  getInventoryReady,
  getSearch
} from "../results/selectors/state-accessors";
import { getSelectedTripFares } from "../results/selectors/trips/get-trip-classes-and-amentities";
import { getDisplayOperator } from "../results/selectors/trips/get-trip-operators";
import { isTripVehicleAvailable } from "../results/selectors/trips/is-trip-vehicle-available";
import { isTripVehicleLoading } from "../results/selectors/trips/is-trip-vehicle-loading";

interface StateProps {
  default_trip_id?: string;
  default_trip_by_journey_id?: string;
  error: boolean;
  inventory_ready: boolean;
  is_amenities_selection_hidden?: boolean;
  is_external: boolean;
  is_loading_vehicle: boolean;
  is_single_class_option?: boolean;
  is_single_fare_option?: boolean;
  is_vehicle_available: boolean | undefined;
  operator_has_ratings: boolean;
  result_search_count: number | null;
  search: SearchInfo;
  trip_options_matrix: TripOptionsMatrix;
  stops_loaded: boolean;
  unique_destinations: ShuttleDestination[];
  search_trip_id: string | null;
}

export interface OwnProps {
  leg_card_id: string;
  journey_id: string | null;
  trip_id?: string | null;
}

export const mapStateToProps = (
  state: ResultsReduxState,
  ownProps: OwnProps
): StateProps => {
  const {
    leg_card_id,
    journey_id,
    trip_id: navigation_trip_id = null
  } = ownProps;
  const inventory_ready = getInventoryReady(state);
  const search = getSearch(state);
  const leg_card = getLegCardById(state, leg_card_id);

  if (!leg_card_id || !leg_card) {
    return {
      error: false,
      inventory_ready,
      is_external: false,
      is_loading_vehicle: false,
      is_vehicle_available: undefined,
      operator_has_ratings: false,
      result_search_count: null,
      search,
      trip_options_matrix: [],
      stops_loaded: false,
      unique_destinations: [],
      search_trip_id: null
    };
  }

  const trip_options_matrix = getLegCardOptionsMatrixForJourney(
    state,
    leg_card_id,
    journey_id
  );

  const search_trip_id =
    navigation_trip_id &&
    trip_options_matrix.some(row =>
      row.some(option => option.trip_id === navigation_trip_id)
    )
      ? navigation_trip_id
      : null;

  const error: boolean = inventory_ready && !trip_options_matrix;

  // identify meme bus / meme train
  const default_trip_by_journey_id: string | undefined = journey_id
    ? getDefaultTripIdByJourneyId(trip_options_matrix, journey_id)
    : undefined;

  const default_trip_id: string | undefined =
    default_trip_by_journey_id ?? getDefaultTripId(trip_options_matrix);

  const trip_classes = trip_options_matrix;
  const fares = getSelectedTripFares(trip_classes, default_trip_id);

  const is_single_fare_option = fares.length === 1;
  const is_single_class_option = trip_classes.length === 1;
  const is_amenities_selection_hidden = shouldHideAmenitiesSelection(
    is_single_class_option,
    trip_classes
  );

  // TODO default_trip_id should never be undefined
  const trip_id = default_trip_id || leg_card[0];
  // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
  const trip = getTripById(state, trip_id);
  // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
  const is_external = isBoXTrip(trip);
  // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
  const is_loading_vehicle = isTripVehicleLoading(state, trip_id);
  // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
  const is_vehicle_available = isTripVehicleAvailable(state, trip_id);

  // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
  const display_operator = getDisplayOperator(state, trip_id);
  // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
  const profile_rating = display_operator.profile_id
    ? // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      getOperatorProfileRatingById(state, display_operator.profile_id)
    : null;

  const operator_has_ratings =
    !!profile_rating && profile_rating.count_total > 0;

  const stops_loaded: boolean =
    trip_id && trip ? areTripStopsLoaded(state, trip_id) : false;

  const result_search_count = getResultSearchCount(state);

  const unique_destinations = getUniqueDestinationsForShuttleLegCard(
    state,
    leg_card_id
  );

  return {
    default_trip_id,
    default_trip_by_journey_id,
    error,
    inventory_ready,
    is_amenities_selection_hidden,
    is_external,
    is_loading_vehicle,
    is_single_class_option,
    is_single_fare_option,
    is_vehicle_available,
    operator_has_ratings,
    result_search_count,
    search,
    trip_options_matrix,
    stops_loaded,
    unique_destinations,
    search_trip_id
  };
};

const connector = connect(mapStateToProps);

export type ConnectorProps = ConnectedProps<typeof connector>;

export const TicketOptions = connector(TicketOptionsComponent);
